import { calcFileIdByteLength, serializationFileId } from "../../modules/files/FileTypes";
import { BusMessage, calcStringByteLength, MessageFactory, calcArrayByteLength, calcFastFloat32ByteLength } from "flavr-common";
/**
 * Carries ROS bag point-cloud data.
 */
export class RosBagPointCloudDataMessage extends BusMessage {
    get byteLength() {
        return calcArrayByteLength(this.vertexArray);
    }
    constructor(vertexArray) {
        super();
        this.vertexArray = vertexArray;
    }
    serialization(stream) {
        this.vertexArray = stream.array(this.vertexArray);
    }
    static is(msg) {
        return msg.type === RosBagPointCloudDataMessage.typestatic;
    }
}
RosBagPointCloudDataMessage.typestatic = "RosBagPointCloudDataMessage";
RosBagPointCloudDataMessage.subjectstatic = "general";
MessageFactory.register(RosBagPointCloudDataMessage);
/**
 * Carries extracted ROS bag point-cloud data.
 */
export class RosBagPointCloudDataExtractedMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.localForageKey);
    }
    constructor(localForageKey) {
        super();
        this.localForageKey = localForageKey;
    }
    serialization(stream) {
        this.localForageKey = stream.utf16String(this.localForageKey);
    }
    static is(msg) {
        return msg.type === RosBagPointCloudDataExtractedMessage.typestatic;
    }
}
RosBagPointCloudDataExtractedMessage.typestatic = "RosBagPointCloudDataExtractedMessage";
RosBagPointCloudDataExtractedMessage.subjectstatic = "general";
MessageFactory.register(RosBagPointCloudDataExtractedMessage);
/**
 * Identifies a ROS bag point-cloud source.
 */
export class RosBagPointCloudIdentifierMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.identifier);
    }
    constructor(identifier) {
        super();
        this.identifier = identifier;
    }
    serialization(stream) {
        this.identifier = stream.utf16String(this.identifier);
    }
    static is(msg) {
        return msg.type === RosBagPointCloudIdentifierMessage.typestatic;
    }
}
RosBagPointCloudIdentifierMessage.typestatic = "RosBagPointCloudIdentifierMessage";
RosBagPointCloudIdentifierMessage.subjectstatic = "general";
MessageFactory.register(RosBagPointCloudIdentifierMessage);
/**
 * Requests the available ROS bag point-cloud topics.
 */
export class RosBagPointCloudTopicsRequestMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.path);
    }
    constructor(path) {
        super();
        this.path = path;
    }
    serialization(stream) {
        this.path = stream.utf16String(this.path);
    }
    static is(msg) {
        return msg.type === RosBagPointCloudTopicsRequestMessage.typestatic;
    }
}
RosBagPointCloudTopicsRequestMessage.typestatic = "RosBagPointCloudTopicsRequestMessage";
RosBagPointCloudTopicsRequestMessage.subjectstatic = "general";
MessageFactory.register(RosBagPointCloudTopicsRequestMessage);
/**
 * Returns the available ROS bag point-cloud topics.
 */
export class RosBagPointCloudTopicsAnswerMessage extends BusMessage {
    get byteLength() {
        return calcArrayByteLength(this.topics);
    }
    constructor(topics) {
        super();
        this.topics = topics;
    }
    serialization(stream) {
        this.topics = stream.array(this.topics);
    }
    static is(msg) {
        return msg.type === RosBagPointCloudTopicsAnswerMessage.typestatic;
    }
}
RosBagPointCloudTopicsAnswerMessage.typestatic = "RosBagPointCloudTopicsAnswerMessage";
RosBagPointCloudTopicsAnswerMessage.subjectstatic = "general";
MessageFactory.register(RosBagPointCloudTopicsAnswerMessage);
export var RosBagMessageType;
(function (RosBagMessageType) {
    RosBagMessageType[RosBagMessageType["play"] = 0] = "play";
    RosBagMessageType[RosBagMessageType["pause"] = 1] = "pause";
    RosBagMessageType[RosBagMessageType["nextTimeStep"] = 2] = "nextTimeStep";
    RosBagMessageType[RosBagMessageType["previousTimeStep"] = 3] = "previousTimeStep";
    RosBagMessageType[RosBagMessageType["sync"] = 4] = "sync";
})(RosBagMessageType || (RosBagMessageType = {}));
/**
 * Controls ROS bag point-cloud playback.
 */
export class RosBagPointCloudPlayerMessage extends BusMessage {
    get byteLength() {
        return (Uint32Array.BYTES_PER_ELEMENT + // command
            Uint32Array.BYTES_PER_ELEMENT); // time
    }
    constructor(command, time) {
        super();
        this.command = command;
        this.time = time;
    }
    serialization(stream) {
        this.command = stream.uint32(this.command);
        this.time = stream.uint32Safe(this.time);
    }
    static is(msg) {
        return msg.type === RosBagPointCloudPlayerMessage.typestatic;
    }
}
RosBagPointCloudPlayerMessage.typestatic = "RosBagPointCloudPlayerMessage";
RosBagPointCloudPlayerMessage.subjectstatic = "general";
MessageFactory.register(RosBagPointCloudPlayerMessage);
/**
 * Requests the current ROS bag playback state.
 */
export class RosBagPointCloudPlayStateRequestMessage extends BusMessage {
    get byteLength() {
        return Uint32Array.BYTES_PER_ELEMENT;
    }
    serialization() { }
    static is(msg) {
        return msg.type === RosBagPointCloudPlayStateRequestMessage.typestatic;
    }
}
RosBagPointCloudPlayStateRequestMessage.typestatic = "RosBagPointCloudPlayStateRequestMessage";
RosBagPointCloudPlayStateRequestMessage.subjectstatic = "general";
MessageFactory.register(RosBagPointCloudPlayStateRequestMessage);
/**
 * Forwards ROS bag point-cloud data from a peer.
 */
export class RosBagPointCloudFromPeerMessage extends BusMessage {
    get byteLength() {
        let length = 0;
        length += calcStringByteLength(this.identifier);
        length += calcStringByteLength(this.frameId);
        length += 3 * Float32Array.BYTES_PER_ELEMENT; // position
        length += 4 * Float32Array.BYTES_PER_ELEMENT; // rotation
        length += Uint32Array.BYTES_PER_ELEMENT; // color
        length += Float32Array.BYTES_PER_ELEMENT; // pointsize
        length += calcFileIdByteLength(this.fileId);
        return length;
    }
    constructor(
    // Relevant data to correctly display remote PointCloud
    identifier, frameId, position, rotation, color, pointSize, fileId) {
        super();
        this.identifier = identifier;
        this.frameId = frameId;
        this.position = position;
        this.rotation = rotation;
        this.color = color;
        this.pointSize = pointSize;
        this.fileId = fileId;
    }
    serialization(stream) {
        this.identifier = stream.utf16String(this.identifier);
        this.frameId = stream.utf16String(this.frameId);
        this.position = stream.number3(this.position);
        this.rotation = stream.number4(this.rotation);
        this.color = stream.uint32(this.color);
        this.pointSize = stream.float32(this.pointSize);
        this.fileId = serializationFileId(stream, this.fileId);
    }
    static is(msg) {
        return msg.type === RosBagPointCloudFromPeerMessage.typestatic;
    }
}
RosBagPointCloudFromPeerMessage.typestatic = "RosBagPointCloudFromPeerMessage";
RosBagPointCloudFromPeerMessage.subjectstatic = "general";
MessageFactory.register(RosBagPointCloudFromPeerMessage);
/**
 * Transfers ROS bag point-cloud float data from a peer.
 */
export class RosBagPointCloudPeerFloat32ArrayMessage extends BusMessage {
    get byteLength() {
        return (calcFastFloat32ByteLength(this.data) +
            3 * Float32Array.BYTES_PER_ELEMENT + // position
            +4 * Float32Array.BYTES_PER_ELEMENT + // rotation
            +Uint32Array.BYTES_PER_ELEMENT + // color
            +Float32Array.BYTES_PER_ELEMENT + // pointsize
            +calcStringByteLength(this.identifier) +
            calcStringByteLength(this.frameId));
    }
    constructor(identifier, frameId, data, position, rotation, color, pointSize) {
        super();
        this.identifier = identifier;
        this.frameId = frameId;
        this.data = data;
        this.position = position;
        this.rotation = rotation;
        this.color = color;
        this.pointSize = pointSize;
    }
    serialization(stream) {
        this.data = stream.fastFloat32Array(this.data);
        this.position = stream.number3(this.position);
        this.rotation = stream.number4(this.rotation);
        this.color = stream.uint32(this.color);
        this.pointSize = stream.float32(this.pointSize);
        this.identifier = stream.utf16String(this.identifier);
        this.frameId = stream.utf16String(this.frameId);
    }
    static is(msg) {
        return msg.type === RosBagPointCloudPeerFloat32ArrayMessage.typestatic;
    }
}
RosBagPointCloudPeerFloat32ArrayMessage.typestatic = "RosBagPointCloudPeerFloat32ArrayMessage";
RosBagPointCloudPeerFloat32ArrayMessage.subjectstatic = "general";
MessageFactory.register(RosBagPointCloudPeerFloat32ArrayMessage);
/**
 * Synchronizes ROS bag point-cloud playback state.
 */
export class RosBagPointCloudSyncMessage extends BusMessage {
    get byteLength() {
        return Uint32Array.BYTES_PER_ELEMENT;
    }
    constructor(serverTimeToStart) {
        super();
        this.serverTimeToStart = serverTimeToStart;
    }
    serialization(stream) {
        this.serverTimeToStart = stream.uint32(this.serverTimeToStart);
    }
    static is(msg) {
        return msg.type === RosBagPointCloudSyncMessage.typestatic;
    }
}
RosBagPointCloudSyncMessage.typestatic = "RosBagPointCloudSyncMessage";
RosBagPointCloudSyncMessage.subjectstatic = "general";
MessageFactory.register(RosBagPointCloudSyncMessage);
/**
 * Requests the current ROS bag playback time.
 */
export class RosBagPointCloudPlaybackTimeRequestMessage extends BusMessage {
    get byteLength() {
        return Uint32Array.BYTES_PER_ELEMENT;
    }
    serialization() { }
    static is(msg) {
        return msg.type === RosBagPointCloudPlaybackTimeRequestMessage.typestatic;
    }
}
RosBagPointCloudPlaybackTimeRequestMessage.typestatic = "RosBagPointCloudPlaybackTimeRequestMessage";
RosBagPointCloudPlaybackTimeRequestMessage.subjectstatic = "general";
MessageFactory.register(RosBagPointCloudPlaybackTimeRequestMessage);
/**
 * Returns the current ROS bag playback time.
 */
export class RosBagPointCloudPlaybackTimeResponseMessage extends BusMessage {
    get byteLength() {
        return Uint32Array.BYTES_PER_ELEMENT;
    }
    constructor(localPlaybackTime) {
        super();
        this.localPlaybackTime = localPlaybackTime;
    }
    serialization(stream) {
        this.localPlaybackTime = stream.float32(this.localPlaybackTime);
    }
    static is(msg) {
        return msg.type === RosBagPointCloudPlaybackTimeResponseMessage.typestatic;
    }
}
RosBagPointCloudPlaybackTimeResponseMessage.typestatic = "RosBagPointCloudPlaybackTimeResponseMessage";
RosBagPointCloudPlaybackTimeResponseMessage.subjectstatic = "general";
MessageFactory.register(RosBagPointCloudPlaybackTimeResponseMessage);
/**
 * Requests available data information for a ROS bag point cloud.
 */
export class RosBagPointCloudAvailableDataRequestMessage extends BusMessage {
    get byteLength() {
        return Uint32Array.BYTES_PER_ELEMENT;
    }
    serialization() { }
    static is(msg) {
        return msg.type === RosBagPointCloudAvailableDataRequestMessage.typestatic;
    }
}
RosBagPointCloudAvailableDataRequestMessage.typestatic = "RosBagPointCloudAvailableDataRequestMessage";
RosBagPointCloudAvailableDataRequestMessage.subjectstatic = "general";
MessageFactory.register(RosBagPointCloudAvailableDataRequestMessage);
/**
 * Deletes a stored point-cloud identifier.
 */
export class DeletePointCloudIdentifierMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.identifier) + Float32Array.BYTES_PER_ELEMENT;
    }
    constructor(uid, identifier) {
        super();
        this.uid = uid;
        this.identifier = identifier;
    }
    serialization(stream) {
        this.uid = stream.float32(this.uid);
        this.identifier = stream.utf16String(this.identifier);
    }
    static is(msg) {
        return msg.type === DeletePointCloudIdentifierMessage.typestatic;
    }
}
DeletePointCloudIdentifierMessage.typestatic = "DeletePointCloudIdentifierMessage";
DeletePointCloudIdentifierMessage.subjectstatic = "general";
MessageFactory.register(DeletePointCloudIdentifierMessage);
